import { useDeleteFileMutation } from "apollo/hooks/mutations";
import {
  FILE_DELETE_QUERY_WITHOUT_FORM_SIDE_EFFECTS,
  FILE_SCOPE,
  QUERY_PROGRESS_FAILED,
  QUERY_PROGRESS_NOT_STARTED,
  QUERY_PROGRESS_PENDING,
  QUERY_PROGRESS_SUCCEED,
  RECARE_FILE_TYPE_TO_CATEGORY,
} from "core/consts";
import { getErrorMessage } from "core/model/utils/errors";
import {
  Auction,
  AuctionForm,
  DocsFormDataMapped,
  DocsFormName,
  FileParams,
  Patient,
  QueryProgress,
} from "core/types";
import {
  FileUploadStates,
  useUploadFile,
} from "dsl/ecosystems/PatientFiles/useUploadFile";
import { useState } from "react";
import { useTranslations } from "translations";
import { PdfConfig } from "./Forms";
import { useCreateFormPdf } from "./Forms/DischargeLetters/useCreateFormPdf";
import { FORM_NAME_TO_FILE_TYPE } from "./utils";

export function useFormPdfUpload<T extends DocsFormName>({
  auction,
  auctionForms,
  fileParams,
  formName,
  patient,
}: {
  auction: Auction | undefined;
  auctionForms: AuctionForm[];
  fileParams: FileParams;
  formName: T;
  patient: Patient;
}) {
  const translations = useTranslations();
  const [uploadStates, setUploadStates] = useState<FileUploadStates>({});
  const [progress, setProgress] = useState<QueryProgress>(
    QUERY_PROGRESS_NOT_STARTED,
  );

  const createFormPdf = useCreateFormPdf({
    auctionForms,
    externalId: patient.external_id ?? "",
    formName,
  });

  const { handleUploadFile } = useUploadFile({
    action: undefined,
    auction,
    params: fileParams,
    setUploadStates,
    uploadStates,
  });

  const [deleteUploadedFile] = useDeleteFileMutation({});

  const uploadFormPdf = async (
    formData: DocsFormDataMapped<T>,
    pdfConfig: PdfConfig<T>,
  ) => {
    setProgress(QUERY_PROGRESS_PENDING);

    let uploadedFileId: number | undefined;

    const cleanupUploadedFile = async () => {
      if (uploadedFileId === undefined) return;
      const id = uploadedFileId;
      uploadedFileId = undefined;
      try {
        await deleteUploadedFile(undefined, {
          id,
          params:
            `${fileParams}&${FILE_DELETE_QUERY_WITHOUT_FORM_SIDE_EFFECTS}` as FileParams,
        });
      } catch (err) {
        console.error(
          "[useDocsFormOnSubmit] Failed to delete orphaned PDF after save failure:",
          getErrorMessage(err),
        );
      }
    };

    const fail = async () => {
      await cleanupUploadedFile();
      setProgress(QUERY_PROGRESS_FAILED);
      return false;
    };

    try {
      const pdfBytes = await createFormPdf<T>({
        documentNumber: -1,
        formData,
        pdfConfig,
      });

      if (!pdfBytes) {
        console.error("could not create pdf on form save");
        return await fail();
      }

      const fileType = FORM_NAME_TO_FILE_TYPE[formName];
      const fileCategory = RECARE_FILE_TYPE_TO_CATEGORY[fileType];
      const pdfFile = new File([pdfBytes], `${formName}.pdf`, {
        type: "application/pdf",
      });
      const base64Content = btoa(
        Array.from(pdfBytes, (byte) =>
          String.fromCharCode(byte as number),
        ).join(""),
      );
      const fileName = pdfConfig.downloadPdfFileName(translations);

      try {
        const uploadedFile = await handleUploadFile({
          category: fileCategory,
          content: base64Content,
          file: pdfFile,
          file_scope: FILE_SCOPE.PATIENT_FILES,
          fileName: `${fileName}.pdf`,
          form_synced_with_file: true,
          kis_document_id_string: undefined,
          recare_file_type: fileType,
        });

        if (!uploadedFile) {
          return await fail();
        }
        uploadedFileId = uploadedFile.id;
      } catch (err) {
        console.error("error uploading pdf", err);
        uploadedFileId = undefined;
        return await fail();
      }

      setProgress(QUERY_PROGRESS_SUCCEED);

      return { fileType, progress, uploadedFileId };
    } catch (err) {
      console.error("error uploading pdf", err);
      uploadedFileId = undefined;
      return await fail();
    }
  };

  return { deleteUploadedFile, progress, uploadFormPdf };
}
